// src/components/shared/enquiryForm/enquiryForm.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import "./enquiryForm.css";
import { sendLeadToSellDo } from "../../../services/selldo";

/**
 * Reusable "We Are Happy to Help" enquiry form.
 * Same fields/validation/submit logic as the banner form,
 * but laid out to sit inline inside a card (no absolute positioning)
 * so it can be reused outside the banner — e.g. in the footer.
 *
 * Pass `redirectTo` to navigate to a page after a successful submit
 * (e.g. a thank-you page). If omitted, the form just resets in place
 * and shows the inline thank-you message, same as before.
 */
export default function EnquiryForm({
  title = "We Are Happy to Help",
  subtitle = "Fill out the details and enquiry now.",
  source = "Enquiry Form",
  redirectTo,
  referrer,
  campaignName,
  onSuccess, // ← NEW
}) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    city: "",
    visitDate: "",
    terms: false,
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updated = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };
    setFormData(updated);
    validateForm(updated);
  };

  const validateForm = (data) => {
    const newErrors = {};
    if (!data.name.trim()) newErrors.name = "Name is required";
    if (!data.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Invalid email address";
    }
    if (!data.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(data.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits";
    }
    if (!data.terms)
      newErrors.terms = "You must agree to the Terms & Conditions";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClick = async () => {
    if (!validateForm(formData)) return;

    setLoading(true);
    try {
      await sendLeadToSellDo({
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        city: formData.city,
        visitDate: formData.visitDate,
        source,
        referrer,
        campaignName,
      });

      setSubmitted(true);
      setShowSuccess(true);
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-16666240151/lu0cCPPHhMYcEJfJios-",
          value: 1.0,
          currency: "INR",
        });
      }
      setFormData({
        name: "",
        email: "",
        mobile: "",
        city: "",
        visitDate: "",
        terms: false,
      });

      if (onSuccess) {
        onSuccess();
      }

      if (redirectTo) {
        // Navigate straight to the thank-you page — no inline alert/reset needed.
        navigate(redirectTo);
      } else {
        alert(
          "Thank you for showing your interest! Our business team will reach you soon.",
        );
        setTimeout(() => {
          setSubmitted(false);
          setShowSuccess(false);
        }, 5000);
      }
    } catch (err) {
      console.error("Enquiry form submission error:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enquiry-form-card">
      {submitted ? (
        <div className="enquiry-thankyou">
          {showSuccess && (
            <p className="enquiry-success-text">
              ✅ Form submitted successfully!
            </p>
          )}
          <p>Thank you for your request! We will get back to you soon.</p>
        </div>
      ) : (
        <>
          <h2 className="enquiry-form-title">{title}</h2>
          <p className="enquiry-form-subtitle">{subtitle}</p>

          {/* Row 1 — Name + Mobile */}
          <div className="enquiry-form-grid">
            <div>
              <div className="enquiry-input-wrap">
                <FaUser className="enquiry-icon" />
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              {errors.name && (
                <div className="enquiry-error">{errors.name}</div>
              )}
            </div>
            <div>
              <div className="enquiry-input-wrap">
                <FaPhone className="enquiry-icon" />
                <input
                  type="text"
                  name="mobile"
                  placeholder="Mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                />
              </div>
              {errors.mobile && (
                <div className="enquiry-error">{errors.mobile}</div>
              )}
            </div>
          </div>

          {/* Row 2 — Email + City */}
          <div className="enquiry-form-grid">
            <div>
              <div className="enquiry-input-wrap">
                <FaEnvelope className="enquiry-icon" />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              {errors.email && (
                <div className="enquiry-error">{errors.email}</div>
              )}
            </div>
            <div>
              <div className="enquiry-input-wrap">
                <FaMapMarkerAlt className="enquiry-icon" />
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Date picker */}
          <div className="enquiry-date-section">
            <p className="enquiry-date-label">
              When are you planning to visit the site?
            </p>
            <div className="enquiry-input-wrap">
              <FaCalendarAlt className="enquiry-icon" />
              <input
                type="date"
                name="visitDate"
                value={formData.visitDate}
                onChange={handleChange}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Checkbox */}
          <div className="enquiry-terms-container">
            <label className="enquiry-terms-label">
              <input
                type="checkbox"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
              />
              <span>
                I authorise Pon Anandham &amp; its representatives to contact me
                with updates and notifications via Email/SMS/What'sApp/Call.
                This will override DND/NDNC.
              </span>
            </label>
            {errors.terms && (
              <div className="enquiry-invalid-feedback">{errors.terms}</div>
            )}
          </div>

          {/* Submit */}
          <button
            className="enquiry-submit-button"
            onClick={handleClick}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Enquiry Now"}
          </button>
        </>
      )}
    </div>
  );
}
